export const onboardingArticles = [
    {
        id: 'onboarding.overview',
        title: 'Onboarding Overview',
        category: 'Getting Started',
        order: 1,
        summary: 'Getting started with Costed.app setup wizard',
        content: `
The onboarding wizard helps new users set up their Costed.app account quickly and efficiently. It guides you through essential configuration steps to get your workspace ready for use.

**When You'll See Onboarding:**

- After creating a new account
- As a new team member joining an existing workspace
- When resetting your workspace configuration

**Onboarding Types:**

- **Owner Setup** - Full configuration for account owners (6 steps)
- **Team Member Setup** - Simplified setup for invited users (3 steps)
    `,
        expandableSections: [
            {
                title: 'Owner Onboarding Steps',
                content: `
Account owners go through these setup steps:

1. **Welcome** - Introduction and overview
2. **Company Profile** - Business details and settings
3. **Cost Centres** - Choose or create cost centre structure
4. **Units of Measure** - Configure measurement units
5. **First Job** - Create your first project (optional)
6. **Invite Team** - Add team members (optional)

You can skip optional steps and complete them later from Settings.
        `,
            },
            {
                title: 'Team Member Onboarding',
                content: `
Invited team members have a shorter onboarding:

1. **Profile Setup** - Personal details and preferences
2. **Role Overview** - Understanding your permissions
3. **Quick Tour** - Brief introduction to key features

Your administrator has already configured the workspace settings.
        `,
            },
            {
                title: 'Skipping and Resuming',
                content: `
**Skipping Steps:**
- Optional steps can be skipped
- A "Skip for Now" button appears on optional steps
- Skipped settings can be configured later in Settings

**Resuming Later:**
- Progress is saved automatically
- If you close the browser, you'll resume where you left off
- Click "Skip Setup" to dismiss entirely and go to dashboard

**Returning to Setup:**
- Access skipped steps from Settings menu
- Look for incomplete setup indicators in the sidebar
        `,
            },
        ],
        keywords: ['onboarding', 'setup', 'getting started', 'wizard', 'new account'],
        route: '/onboarding',
        relatedArticles: ['onboarding.company-profile', 'onboarding.cost-centres', 'settings.overview'],
    },
    {
        id: 'onboarding.company-profile',
        title: 'Setting Up Company Profile',
        category: 'Getting Started',
        order: 2,
        summary: 'Configure your company details during onboarding',
        content: `
The Company Profile step collects essential business information that appears on documents, reports, and communications.

**Required Information:**

- Company name
- Business address
- Contact details (phone, email)

**Optional Information:**

- Company logo
- ABN/Business number
- Industry type
    `,
        expandableSections: [
            {
                title: 'Uploading Your Logo',
                content: `
Add your company logo to personalize documents:

1. Click the logo upload area
2. Select an image file (PNG, JPG, or SVG)
3. Crop or adjust if needed
4. Click "Upload"

**Logo Requirements:**
- Recommended size: 200x100 pixels minimum
- Supported formats: PNG, JPG, SVG
- Maximum file size: 2MB
- Transparent backgrounds work best
        `,
            },
            {
                title: 'Address Format',
                content: `
Enter your business address in the standard format:

- Street address (line 1)
- Suite/Unit (line 2, optional)
- City/Suburb
- State/Province
- Postcode
- Country

This address appears on purchase orders, invoices, and reports.
        `,
            },
        ],
        keywords: ['company profile', 'business details', 'logo', 'address', 'setup'],
        relatedArticles: ['onboarding.overview', 'settings.company'],
    },
    {
        id: 'onboarding.cost-centres',
        title: 'Choosing Cost Centre Structure',
        category: 'Getting Started',
        order: 3,
        summary: 'Select a cost centre template or start blank',
        content: `
Cost centres organize your BOQ items into logical sections. During onboarding, you can choose from pre-built templates or start with a blank structure.

**Available Templates:**

- **Simple (Australian)** - 12 standard cost centres for residential/commercial
- **UniFormat II** - 8 major categories following international standard
- **Uniclass 2015** - 14 system categories for UK/international projects
- **Start Blank** - Create your own structure later
    `,
        expandableSections: [
            {
                title: 'Simple (Australian) Template',
                content: `
The Simple template includes 12 common cost centres:

1. Preliminaries
2. Site Works
3. Substructure
4. Structure
5. External Walls
6. Windows & Doors
7. Internal Walls
8. Internal Finishes
9. Fittings & Equipment
10. Services
11. External Works
12. Contingencies

Best for: Australian residential and commercial builders.
        `,
            },
            {
                title: 'UniFormat II Template',
                content: `
UniFormat II follows the international ASTM standard:

- A - Substructure
- B - Shell
- C - Interiors
- D - Services
- E - Equipment & Furnishings
- F - Special Construction
- G - Building Sitework
- Z - General Requirements

Best for: International projects, commercial construction.
        `,
            },
            {
                title: 'Uniclass 2015 Template',
                content: `
Uniclass 2015 provides detailed system categories:

- Structural Systems
- Wall & Barrier Systems
- Roof Systems
- Floor Systems
- Electrical Systems
- And 9 more...

Best for: UK projects, BIM-integrated workflows.
        `,
            },
            {
                title: 'Customizing Later',
                content: `
Regardless of which template you choose:

- Add new cost centres anytime
- Rename existing cost centres
- Create subgroups for detail
- Reorder to match your workflow

Go to **Settings > Cost Centres** to customize.
        `,
            },
        ],
        keywords: ['cost centres', 'template', 'uniformat', 'uniclass', 'structure'],
        relatedArticles: ['onboarding.overview', 'boq.cost-centres', 'settings.cost-centres'],
    },
    {
        id: 'onboarding.units',
        title: 'Setting Up Units of Measure',
        category: 'Getting Started',
        order: 4,
        summary: 'Configure default units for your catalogue and BOQ',
        content: `
Units of measure define how quantities are entered and calculated in your BOQ. The onboarding wizard can seed default units or you can configure them later.

**Default Units Include:**

- Linear: M (Metre), MM (Millimetre), LM (Lin Metre)
- Area: M2 (Square Metre)
- Volume: M3 (Cubic Metre)
- Count: EA (Each), NO (Number), SET (Set)
- Weight: KG (Kilogram), TN (Tonne)
- Time: HR (Hour), DAY (Day), WK (Week)
- Special: %, ITEM, LOT, H1/H2 (Headers)
    `,
        expandableSections: [
            {
                title: 'Calculation Routines',
                content: `
Each unit has a calculation routine that determines how totals are computed:

- **Standard** - Quantity × Unit Price
- **Divisor-based** - (Quantity ÷ Divisor) × Unit Price
- **Price Only** - Just the unit price (for items)
- **Percentage** - Calculate as % of running total
- **Heading** - Section header, no calculation

These are pre-configured for default units.
        `,
            },
            {
                title: 'Adding Custom Units',
                content: `
After setup, you can add custom units:

1. Go to **Settings > Units**
2. Click **Add Unit**
3. Enter code, name, and calculation settings
4. Save

Custom units appear in the catalogue and BOQ.
        `,
            },
        ],
        keywords: ['units', 'measurement', 'metre', 'square metre', 'calculation'],
        relatedArticles: ['onboarding.overview', 'features.calculations', 'settings.units'],
    },
    {
        id: 'onboarding.first-job',
        title: 'Creating Your First Job',
        category: 'Getting Started',
        order: 5,
        summary: 'Create a sample project during onboarding',
        content: `
The First Job step helps you create your first project to see how Costed.app works. This step is optional but recommended for new users.

**What You'll Configure:**

- Job number format
- Project name and address
- Client details (optional)
- Start and end dates (optional)
    `,
        expandableSections: [
            {
                title: 'Job Number Format',
                content: `
Choose how job numbers are generated:

- **Auto-increment** - 1001, 1002, 1003...
- **Year-based** - 2026-001, 2026-002...
- **Custom prefix** - PRJ-001, EST-001...

This sets the pattern for all future jobs.
        `,
            },
            {
                title: 'Skipping This Step',
                content: `
If you skip creating a first job:

- You can create jobs anytime from the Jobs page
- Sample data won't be generated
- You'll start with a blank workspace

Click "Skip for Now" to proceed without a job.
        `,
            },
        ],
        keywords: ['first job', 'create job', 'project', 'setup'],
        relatedArticles: ['onboarding.overview', 'jobs.overview'],
    },
    {
        id: 'onboarding.invite-team',
        title: 'Inviting Team Members',
        category: 'Getting Started',
        order: 6,
        summary: 'Add colleagues to your Costed.app workspace',
        content: `
The final onboarding step lets you invite team members to collaborate. Each team member will receive an email invitation to join your workspace.

**Invitation Options:**

- Enter email addresses for team members
- Assign initial roles (Admin, Estimator, Viewer)
- Add a personal message (optional)
    `,
        expandableSections: [
            {
                title: 'User Roles',
                content: `
Assign appropriate roles to team members:

**Admin**
- Full access to all features
- Can manage users and settings
- Can delete jobs and data

**Estimator**
- Create and edit BOQs
- Manage catalogue items
- Create orders and documents

**Viewer**
- View-only access
- Can run reports
- Cannot make changes
        `,
            },
            {
                title: 'Invitation Process',
                content: `
When you send invitations:

1. Team members receive an email
2. They click the link to accept
3. They complete brief onboarding
4. They can start collaborating

Pending invitations can be managed in **Settings > Users**.
        `,
            },
            {
                title: 'Skipping Invitations',
                content: `
If you work alone or want to invite later:

- Click "Skip for Now"
- Add team members anytime from Settings
- No limit on when invitations can be sent
        `,
            },
        ],
        keywords: ['invite', 'team', 'users', 'collaboration', 'roles'],
        relatedArticles: ['onboarding.overview', 'settings.users'],
    },
];
